import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Users, 
  Calendar, 
  Upload, 
  CheckSquare, 
  MessageSquare,
  Building,
  MapPin,
  DollarSign,
  Settings,
  Plus,
  Search,
  Mail
} from 'lucide-react';

// Define the reference field configuration interface
export interface RefConfig {
  ref_table: string;
  ref_col: string;
  ref_lookup_col?: string;
  ref_filter_group?: RefFilterGroup;
  intermediate_tables?: IntermediateTableConfig[];
  field_type?: 'TEXT' | 'NUMBER' | 'DATE' | 'CHECKBOX' | 'RADIOBUTTON' | 'USER_ASSIGNMENT' | 'FILE_UPLOAD' | 'MULTI_SELECT' | 'MULTISELECT' | 'USER_SELECT';
  is_editable?: boolean;
  direct_reference?: boolean;
  // Source metadata for cross-checklist references (for display purposes)
  source_item_name?: string;
  source_template_name?: string;
  source_item_type?: string;
}

interface RefFilterGroup {
  col_name: string;
  operator: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'ILIKE' | 'IN' | 'IS NULL' | 'IS NOT NULL';
  value: string | number | boolean | null;
}

interface IntermediateTableConfig {
  table: string;
  intermediate_column: string;
  target_column: string;
}

// Template definitions
export const referenceFieldTemplates: Record<string, {
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  config: RefConfig;
  tags: string[];
}> = {
  // Document Templates
  projectDocuments: {
    name: "Project Documents",
    description: "Reference all documents uploaded to this project",
    icon: <FileText className="h-5 w-5" />,
    category: "Documents",
    config: {
      ref_table: "documents",
      ref_col: "storage_path",
      ref_lookup_col: "project_id",
      field_type: "FILE_UPLOAD",
      is_editable: true
    },
    tags: ["documents", "files", "uploads"]
  },
  
  newProposalFiles: {
    name: "New Proposal Files",
    description: "Reference new proposal files specifically",
    icon: <FileText className="h-5 w-5" />,
    category: "Documents",
    config: {
      ref_table: "documents",
      ref_col: "storage_path",
      ref_lookup_col: "project_id",
      field_type: "FILE_UPLOAD",
      is_editable: true,
      ref_filter_group: {
        col_name: "description",
        operator: "=",
        value: "New Proposal"
      }
    },
    tags: ["documents", "proposals", "files"]
  },
  
  newSalesAgreementFiles: {
    name: "New Sales Agreement Files",
    description: "Reference new sales agreement files specifically",
    icon: <FileText className="h-5 w-5" />,
    category: "Documents",
    config: {
      ref_table: "documents",
      ref_col: "storage_path",
      ref_lookup_col: "project_id",
      field_type: "FILE_UPLOAD",
      is_editable: true,
      ref_filter_group: {
        col_name: "description",
        operator: "=",
        value: "New Sales Agreement"
      }
    },
    tags: ["documents", "sales_agreements", "files"]
  },
  
  proposalFiles: {
    name: "Proposal Files",
    description: "Reference proposal files (excluding new proposals)",
    icon: <FileText className="h-5 w-5" />,
    category: "Documents",
    config: {
      ref_table: "documents",
      ref_col: "storage_path",
      ref_lookup_col: "project_id",
      field_type: "FILE_UPLOAD",
      is_editable: true,
      ref_filter_group: {
        col_name: "description",
        operator: "=",
        value: "Proposal"
      }
    },
    tags: ["documents", "proposals", "files"]
  },

  // Partner Info Templates (single entry with inline field picker)
  
  designFiles: {
    name: "Design Files",
    description: "Reference design-related documents",
    icon: <FileText className="h-5 w-5" />,
    category: "Documents",
    config: {
      ref_table: "documents",
      ref_col: "storage_path",
      ref_lookup_col: "project_id",
      field_type: "FILE_UPLOAD",
      is_editable: true,
      ref_filter_group: {
        col_name: "description",
        operator: "=",
        value: "Design Image"
      }
    },
    tags: ["documents", "design", "files"]
  },
  
  sitePhotos: {
    name: "Site Photos",
    description: "Reference site photos and images",
    icon: <FileText className="h-5 w-5" />,
    category: "Documents",
    config: {
      ref_table: "documents",
      ref_col: "storage_path",
      ref_lookup_col: "project_id",
      field_type: "FILE_UPLOAD",
      is_editable: true,
      ref_filter_group: {
        col_name: "description",
        operator: "=",
        value: "Site Photo"
      }
    },
    tags: ["documents", "photos", "images"]
  },
  
  // Contact Templates
  projectContacts: {
    name: "Project Contacts",
    description: "Reference contacts associated with this project",
    icon: <Users className="h-5 w-5" />,
    category: "Contacts",
    config: {
      ref_table: "contacts",
      ref_col: "first_name",
      ref_lookup_col: "project_id",
      field_type: "TEXT",
      is_editable: true
    },
    tags: ["contacts", "people", "communication"]
  },
  
  primaryContact: {
    name: "Primary Contact",
    description: "Reference the primary contact for this project",
    icon: <Users className="h-5 w-5" />,
    category: "Contacts",
    config: {
      ref_table: "contacts",
      ref_col: "first_name",
      ref_lookup_col: "project_id",
      field_type: "TEXT",
      is_editable: true,
      ref_filter_group: {
        col_name: "is_primary",
        operator: "=",
        value: true
      }
    },
    tags: ["contacts", "primary", "main"]
  },
  
  // Task Templates
  projectTasks: {
    name: "Project Tasks",
    description: "Reference tasks associated with this project",
    icon: <CheckSquare className="h-5 w-5" />,
    category: "Tasks",
    config: {
      ref_table: "tasks",
      ref_col: "title",
      ref_lookup_col: "project_id",
      field_type: "TEXT",
      is_editable: true
    },
    tags: ["tasks", "project management", "work"]
  },
  
  pendingTasks: {
    name: "Pending Tasks",
    description: "Reference pending tasks for this project",
    icon: <CheckSquare className="h-5 w-5" />,
    category: "Tasks",
    config: {
      ref_table: "tasks",
      ref_col: "title",
      ref_lookup_col: "project_id",
      field_type: "TEXT",
      is_editable: true,
      ref_filter_group: {
        col_name: "status",
        operator: "=",
        value: "pending"
      }
    },
    tags: ["tasks", "pending", "incomplete"]
  },
  
  // Communication Templates
  projectMessages: {
    name: "Project Messages",
    description: "Reference messages and communications",
    icon: <MessageSquare className="h-5 w-5" />,
    category: "Communication",
    config: {
      ref_table: "messages",
      ref_col: "content",
      ref_lookup_col: "project_id",
      field_type: "TEXT",
      is_editable: true
    },
    tags: ["messages", "communication", "chat"]
  },
  
  // Project Info Templates
  projectAddress: {
    name: "Project Address",
    description: "Reference the project's address information",
    icon: <MapPin className="h-5 w-5" />,
    category: "Project Info",
    config: {
      ref_table: "projects",
      ref_col: "project_street",
      ref_lookup_col: "id",
      field_type: "TEXT",
      is_editable: true
    },
    tags: ["address", "location", "property"]
  },
  
  projectCost: {
    name: "Project Cost",
    description: "Reference the project's cost information",
    icon: <DollarSign className="h-5 w-5" />,
    category: "Project Info",
    config: {
      ref_table: "projects",
      ref_col: "system_cost",
      ref_lookup_col: "id",
      field_type: "NUMBER",
      is_editable: true
    },
    tags: ["cost", "finance", "budget"]
  },
  
  // Team Templates
  assignedTeam: {
    name: "Assigned Team",
    description: "Reference the team assigned to this project",
    icon: <Users className="h-5 w-5" />,
    category: "Team",
    config: {
      ref_table: "teams",
      ref_col: "name",
      ref_lookup_col: "id",
      field_type: "TEXT",
      is_editable: true,
      intermediate_tables: [
        {
          table: "project_teams",
          intermediate_column: "team_id",
          target_column: "project_id"
        }
      ]
    },
    tags: ["team", "assignment", "group"]
  },
  
  // Custom Templates
  checklistItemReference: {
    name: "Checklist Item Reference",
    description: "Reference the value from another checklist item (choose item after selecting)",
    icon: <CheckSquare className="h-5 w-5" />,
    category: "Checklists",
    config: {
      ref_table: 'project_checklist_items',
      ref_col: 'value',
      ref_lookup_col: 'project_id',
      field_type: 'TEXT',
      is_editable: false,
      direct_reference: true,
      // Placeholder filter; will be set in the next step (custom configuration)
      ref_filter_group: {
        col_name: 'template_item_id',
        operator: '=',
        value: 0
      }
    },
    tags: ["checklists", "reference", "cross-checklist"]
  },
  partnerInfoReference: {
    name: "Partner Info Reference",
    description: "Reference a field from the assigned installer partner (choose field after selecting)",
    icon: <Building className="h-5 w-5" />,
    category: "Partner Info",
    config: {
      // Base config points at installer_partners via project assignment; field (ref_col) chosen inline
      ref_table: 'installer_partners',
      ref_col: '',
      ref_lookup_col: 'id',
      field_type: 'TEXT',
      is_editable: false,
      direct_reference: false,
      intermediate_tables: [
        {
          table: 'projects',
          intermediate_column: 'assigned_installer_partner_id',
          target_column: 'id'
        }
      ]
    },
    tags: ["partner", "installer", "reference"]
  },
  partnerInfoItem: {
    name: "Partner Info Item",
    description: "Reference a field from Partner Info (choose field after selecting)",
    icon: <Building className="h-5 w-5" />,
    category: "Partner Info",
    config: {
      ref_table: 'installer_partners',
      ref_col: '',
      ref_lookup_col: 'id',
      field_type: 'TEXT',
      is_editable: false,
      direct_reference: false,
      intermediate_tables: [
        {
          table: 'projects',
          intermediate_column: 'assigned_installer_partner_id',
          target_column: 'id'
        }
      ]
    },
    tags: ["partner", "info", "item"]
  },
  projectRoleReference: {
    name: "Project Role Reference",
    description: "Reference a person from Partner Info (choose role and field after selecting)",
    icon: <Users className="h-5 w-5" />,
    category: "Partner Info",
    config: {
      ref_table: 'profiles',
      ref_col: '',
      ref_lookup_col: 'id',
      field_type: 'TEXT',
      is_editable: false,
      direct_reference: false,
      // Placeholder; role selection will update intermediate column
      intermediate_tables: [
        { table: 'projects', intermediate_column: 'assigned_internal_pm_id', target_column: 'id' }
      ]
    },
    tags: ["partner", "role", "profile"]
  },
  
};

interface ReferenceFieldTemplatesProps {
  onSelectTemplate: (templateKey: string, config: RefConfig) => void;
  onCustomConfig: () => void;
}

export function ReferenceFieldTemplates({ onSelectTemplate, onCustomConfig }: ReferenceFieldTemplatesProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Get unique categories
  const categories = ["all", ...Array.from(new Set(Object.values(referenceFieldTemplates).map(t => t.category)))];
  
  // Filter templates based on category and search
  const filteredTemplates = Object.entries(referenceFieldTemplates).filter(([key, template]) => {
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Reference Field Templates</h2>
          <p className="text-muted-foreground">
            Choose from pre-built templates or create a custom configuration
          </p>
        </div>
        <Button onClick={onCustomConfig} variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Custom Config
        </Button>
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Category Filter */}
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === "all" ? "All Categories" : category}
              </option>
            ))}
          </select>
        </div>
        
        {/* Search */}
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
            />
          </div>
        </div>
      </div>
      
      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.map(([key, template]) => (
          <Card 
            key={key} 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onSelectTemplate(key, template.config)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="text-blue-600">
                    {template.icon}
                  </div>
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {template.category}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground mb-3">
                {template.description}
              </p>
              <div className="flex flex-wrap gap-1">
                {template.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {filteredTemplates.length === 0 && (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No templates found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search or category filters
          </p>
        </div>
      )}
    </div>
  );
}
